import { useState, useRef, useEffect, useCallback } from "react";
import "./PF.css";

/* ========================
   FORMATTERS
======================== */
const fmt = n => '₹' + Math.round(n).toLocaleString('en-IN');
const fmtShort = n => {
  if (n >= 1e7) return '₹' + (n / 1e7).toFixed(2) + ' Cr';
  if (n >= 1e5) return '₹' + (n / 1e5).toFixed(2) + ' L';
  return '₹' + Math.round(n).toLocaleString('en-IN');
};

/* ========================
   SLIDER
======================== */
function Slider({ value, min, max, step, onChange }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <input
      type="range"
      className="pf-slider"
      min={min} max={max} step={step} value={value}
      onChange={e => onChange(+e.target.value)}
      style={{
        background: `linear-gradient(to right, #263012 0%, #263012 ${pct}%, #e8e8e8 ${pct}%, #e8e8e8 100%)`
      }}
    />
  );
}

/* ========================
   DONUT CHART
======================== */
function DonutChart({ emp, er, interest, vpf, total }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = 180, H = 180, cx = 90, cy = 90, R = 72, r = 44;
    ctx.clearRect(0, 0, W, H);
    if (!total) {
      ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.strokeStyle = '#f0f0f0'; ctx.lineWidth = 24; ctx.stroke(); return;
    }
    const slices = [
      { val: emp, color: '#263012' },
      { val: er, color: '#333' },
      { val: interest, color: '#f5a623' },
      { val: vpf > 0 ? vpf : 0, color: '#888' },
    ].filter(s => s.val > 0);
    const sum = slices.reduce((a, s) => a + s.val, 0);
    let angle = -Math.PI / 2;
    slices.forEach(s => {
      const sweep = (s.val / sum) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, R, angle, angle + sweep);
      ctx.closePath();
      ctx.fillStyle = s.color;
      ctx.fill();
      ctx.beginPath();
      ctx.arc(cx, cy, R, angle, angle + sweep);
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2.5;
      ctx.stroke();
      angle += sweep;
    });
    // inner hole
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = '#fff';
    ctx.fill();
    // center text
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#999';
    ctx.font = '500 11px "Nunito Sans", sans-serif';
    ctx.fillText('Total', cx, cy - 10);
    ctx.fillStyle = '#263012';
    ctx.font = 'bold 12px "Nunito Sans", sans-serif';
    ctx.fillText(fmtShort(total), cx, cy + 8);
  }, [emp, er, interest, vpf, total]);
  return <canvas ref={canvasRef} width={180} height={180} className="pf-donut-canvas" />;
}

/* ========================
   INPUT FIELD
======================== */
function InputField({ label, hint, value, onChange, min, max, step = 1, hasRupee = false, hasSlider = false, sliderMax, suffix }) {
  return (
    <div className="pf-field">
      <div className="pf-field__header">
        <span className="pf-field__label">{label}</span>
        {hint && <span className="pf-field__hint">{hint}</span>}
      </div>
      <div className={`pf-field__input-wrap${hasRupee ? ' pf-field__input-wrap--rupee' : ''}`}>
        {hasRupee && <span className="pf-field__rupee">₹</span>}
        <input
          className="pf-field__input"
          type="number"
          value={value}
          min={min} max={max} step={step}
          onChange={e => onChange(+e.target.value)}
        />
      </div>
      {hasSlider && (
        <div className="pf-field__slider-row">
          <Slider value={value} min={min} max={sliderMax || max} step={step} onChange={onChange} />
          <span className="pf-field__bubble">
            {hasRupee ? fmtShort(value) : `${value}${suffix || ''}`}
          </span>
        </div>
      )}
    </div>
  );
}

/* ========================
   MAIN COMPONENT
======================== */
export default function PF() {
  const [salary, setSalary] = useState(30010);
  const [age, setAge] = useState(30);
  const [retAge, setRetAge] = useState(60);
  const [existing, setExisting] = useState(0);
  const [increment, setIncrement] = useState(5);
  const [rate, setRate] = useState(8.25);
  const [vpfOn, setVpfOn] = useState(false);
  const [vpfAmt, setVpfAmt] = useState(2000);
  const [result, setResult] = useState(null);

  const calculate = useCallback(() => {
    if (age >= retAge) return;
    const years = retAge - age;
    const monthlyRate = rate / 100 / 12;
    const incrFactor = 1 + increment / 100;
    const EPS_CAP = 13001;
    let balance = existing;
    let totEmp = 0, totEr = 0, totInt = 0, totVpf = 0, totEps = 0;
    const rows = [];
    for (let y = 0; y < years; y++) {
      const sal = salary * Math.pow(incrFactor, y);
      const empM = sal * 0.12;
      const epsM = Math.min(sal, EPS_CAP) * 0.0833;
      const erM = sal * 0.12 - epsM;
      const addM = empM + erM + (vpfOn ? vpfAmt : 0);
      let yEmp = 0, yEr = 0, yInt = 0, yVpf = 0, yEps = 0;
      for (let m = 0; m < 12; m++) {
        const intM = balance * monthlyRate;
        balance += addM + intM;
        yEmp += empM; yEr += erM; yInt += intM;
        yVpf += (vpfOn ? vpfAmt : 0); yEps += epsM;
      }
      totEmp += yEmp; totEr += yEr; totInt += yInt; totVpf += yVpf; totEps += yEps;
      rows.push({ year: 2026 + y, sal, empC: yEmp + yVpf, erC: yEr, int: yInt, bal: balance });
    }
    const empM0 = salary * 0.12 + (vpfOn ? vpfAmt : 0);
    const epsM0 = Math.min(salary, EPS_CAP) * 0.0833;
    const erM0 = salary * 0.12 - epsM0;
    setResult({
      corpus: balance, totEmp: totEmp + totVpf, totEr, totInt, totEps, totVpf, rows,
      bar: { empM: empM0, erM: erM0, epsM: epsM0, total: empM0 + erM0 + epsM0 },
      years
    });
  }, [salary, age, retAge, existing, increment, rate, vpfOn, vpfAmt]);

  useEffect(() => { calculate(); }, [calculate]);

  const barItems = result ? [
    { label: 'Employee EPF (12%)', val: result.bar.empM, color: '#263012' },
    { label: 'Employer → EPF (3.67%)', val: result.bar.erM, color: '#333' },
    { label: 'Employer → EPS (8.33%)', val: result.bar.epsM, color: '#888' },
    ...(vpfOn && vpfAmt > 0 ? [{ label: 'VPF Extra', val: vpfAmt, color: '#f5a623' }] : [])
  ] : [];

  return (
    <div className="pf-page">

      {/* ── HERO ── */}
      <header className="pf-hero">
        <div className="pf-hero__inner">
          <div className="pf-hero__eyebrow">EPFO 2026 · India</div>
     <h1 className="pf-hero__title hero-title">
  EPF <span className="choose-title-accent">Retirement Planner</span>
</h1>
          <p className="pf-hero__sub">Calculate your EPF corpus, employer contributions &amp; interest — accurately &amp; instantly</p>
          <div className="pf-hero__tags">
            {['EPFO 2026 Rules', 'EPF + EPS Breakdown', 'VPF Support', 'Salary Increment', 'Section 80C Tax Free'].map(t => (
              <span className="pf-hero__tag" key={t}>{t}</span>
            ))}
          </div>
        </div>
             <div className="nps-hero-ded" style={{marginTop:"25px"}}>
          <div className="nps-ded-item"><span className="nps-ded-val">8.25%</span><span>Interest FY26</span></div>
          <div className="nps-ded-sep">|</div>
          <div className="nps-ded-item"><span className="nps-ded-val">12%</span><span>Employee Share</span></div>
          <div className="nps-ded-sep">|</div>
          <div className="nps-ded-item"><span className="nps-ded-val">3.67%</span><span>Employer EPF</span></div>
           <div className="nps-ded-sep">|</div>
          <div className="nps-ded-item"><span className="nps-ded-val">8.33%</span><span>EPS Share</span></div>
        </div>
      </header>

      {/* ── STAT STRIP ── */}


      {/* ── MAIN GRID ── */}
      <main className="pf-main">

        {/* INPUT CARD */}
        <div className="pf-card">
          <div className="pf-card__heading">
            <span className="pf-card__heading-icon">⚙️</span> Your Details
          </div>

          {/* Salary */}
          <div className="pf-field">
            <div className="pf-field__header">
              <span className="pf-field__label">Basic Salary + DA</span>
              <span className="pf-field__hint">per month</span>
            </div>
            <div className="pf-field__input-wrap pf-field__input-wrap--rupee">
              <span className="pf-field__rupee">₹</span>
              <input className="pf-field__input" type="number" value={salary} min={1000} max={10000000}
                onChange={e => setSalary(+e.target.value)} />
            </div>
            <div className="pf-field__slider-row">
              <Slider value={Math.min(salary, 300000)} min={3001} max={300000} step={1000} onChange={setSalary} />
              <span className="pf-field__bubble">{fmtShort(salary)}</span>
            </div>
          </div>

          {/* Age only slider */}
          <div className="pf-field">
            <div className="pf-field__header">
              <span className="pf-field__label">Current Age</span>
              <span className="pf-field__hint">years</span>
            </div>
            <div className="pf-field__slider-row">
              <Slider value={age} min={18} max={57} step={1} onChange={setAge} />
              <span className="pf-field__bubble">{age} yrs</span>
            </div>
          </div>

          {/* Retirement Age */}
          <div className="pf-field">
            <div className="pf-field__header">
              <span className="pf-field__label">Retirement Age</span>
              <span className="pf-field__hint">58–60 as per EPFO</span>
            </div>
            <div className="pf-field__slider-row">
              <Slider value={retAge} min={50} max={60} step={1} onChange={setRetAge} />
              <span className="pf-field__bubble">{retAge} yrs</span>
            </div>
          </div>

          {/* Existing Balance */}
          <div className="pf-field">
            <div className="pf-field__header">
              <span className="pf-field__label">Current PF Balance</span>
              <span className="pf-field__hint">if any</span>
            </div>
            <div className="pf-field__input-wrap pf-field__input-wrap--rupee">
              <span className="pf-field__rupee">₹</span>
              <input className="pf-field__input" type="number" value={existing} min={0}
                onChange={e => setExisting(+e.target.value)} />
            </div>
          </div>

          {/* Annual Increment */}
          <div className="pf-field">
            <div className="pf-field__header">
              <span className="pf-field__label">Annual Salary Increment</span>
              <span className="pf-field__hint">% per year</span>
            </div>
            <div className="pf-field__slider-row">
              <Slider value={increment} min={0} max={20} step={0.5} onChange={setIncrement} />
              <span className="pf-field__bubble">{increment}%</span>
            </div>
          </div>

          {/* EPF Rate */}
          <div className="pf-field">
            <div className="pf-field__header">
              <span className="pf-field__label">EPF Interest Rate</span>
              <span className="pf-field__hint">% per annum</span>
            </div>
            <div className="pf-field__slider-row">
              <Slider value={rate} min={6} max={12} step={0.05} onChange={setRate} />
              <span className="pf-field__bubble">{rate}%</span>
            </div>
          </div>

          <div className="pf-divider" />

          {/* VPF Toggle */}
          <div className="pf-vpf-row">
            <span className="pf-vpf-row__label">Voluntary PF (VPF)</span>
            <button
              className={`pf-toggle${vpfOn ? ' pf-toggle--on' : ''}`}
              onClick={() => setVpfOn(v => !v)}
              aria-label="Toggle VPF"
            >
              <span className="pf-toggle__thumb" />
            </button>
          </div>

          {vpfOn && (
            <div className="pf-field pf-field--indent">
              <div className="pf-field__header">
                <span className="pf-field__label">VPF Extra Amount</span>
                <span className="pf-field__hint">per month</span>
              </div>
              <div className="pf-field__input-wrap pf-field__input-wrap--rupee">
                <span className="pf-field__rupee">₹</span>
                <input className="pf-field__input" type="number" value={vpfAmt} min={0}
                  onChange={e => setVpfAmt(+e.target.value)} />
              </div>
            </div>
          )}

          {/* Info note */}
          <div className="pf-info-note">
            <strong>EPFO 2026:</strong> Employee <strong>12%</strong> of Basic+DA. Employer: <strong>8.33%</strong> → EPS (capped ₹1,250/mo) + <strong>3.67%</strong> → EPF. Rate: <strong>8.25% p.a.</strong> FY 2025–26. EEE — fully tax-free up to ₹2.5L/yr.
          </div>
        </div>

        {/* OUTPUT PANEL */}
        <div className="pf-output">

          {/* Corpus Hero Card */}
          <div className="pf-corpus-card">
            <div className="pf-corpus-card__label">Total EPF Corpus at Retirement</div>
            <div className="pf-corpus-card__amount">
              {result ? fmtShort(result.corpus) : '₹ –'}
            </div>
            <div className="pf-corpus-card__meta">
              {result
                ? `Over ${result.years} years · Retirement ${2026 + result.years} · Rate ${rate}% p.a.`
                : 'Adjust inputs to calculate'}
            </div>
            <div className="pf-corpus-card__stats">
              {[
                ['👤 Your Contributions', result?.totEmp],
                ['🏢 Employer (EPF)', result?.totEr],
                ['📈 Interest Earned', result?.totInt],
                ['🛡️ EPS Pension Fund', result?.totEps],
              ].map(([lbl, val]) => (
                <div className="pf-corpus-stat" key={lbl}>
                  <div className="pf-corpus-stat__label">{lbl}</div>
                  <div className="pf-corpus-stat__value">{val ? fmtShort(val) : '₹–'}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Monthly Breakdown */}
          <div className="pf-card pf-card--sm">
            <div className="pf-card__heading">
              <span className="pf-card__heading-icon">📊</span> Monthly Contribution Breakdown
            </div>
            <div className="pf-bars">
              {barItems.map(({ label, val, color }) => {
                const total = result?.bar.total || 1;
                const pct = Math.max(2, (val / (total + (vpfOn ? vpfAmt : 0))) * 100);
                return (
                  <div className="pf-bar-row" key={label}>
                    <div className="pf-bar-row__top">
                      <span className="pf-bar-row__label">{label}</span>
                      <span className="pf-bar-row__val">{fmt(val)}/mo</span>
                    </div>
                    <div className="pf-bar-track">
                      <div className="pf-bar-fill" style={{ width: pct + '%', background: color }} />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="pf-divider" />

            {/* Donut + Legend */}
            <div className="pf-donut-section">
              <DonutChart
                emp={result ? result.totEmp - (vpfOn ? result.totVpf : 0) : 0}
                er={result?.totEr || 0}
                interest={result?.totInt || 0}
                vpf={vpfOn && result ? result.totVpf : 0}
                total={result?.corpus || 0}
              />
              <div className="pf-legend">
                {[
                  { color: '#263012', label: 'Your Contributions', val: result?.totEmp },
                  { color: '#333', label: 'Employer (EPF)', val: result?.totEr },
                  { color: '#f5a623', label: 'Total Interest', val: result?.totInt },
                  ...(vpfOn && result ? [{ color: '#888', label: 'VPF Extra', val: result?.totVpf }] : [])
                ].map(({ color, label, val }) => (
                  <div className="pf-legend-item" key={label}>
                    <span className="pf-legend-dot" style={{ background: color }} />
                    <div>
                      <div className="pf-legend-item__label">{label}</div>
                      <div className="pf-legend-item__val">{val ? fmtShort(val) : '–'}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="pf-tax-badge">
              ✅ EEE Status: Contributions + Interest + Maturity — All Tax-Free under Sec 80C (up to ₹1.5L/yr)
            </div>
          </div>

          {/* Year Table */}
          <div className="pf-card pf-card--sm">
            <div className="pf-card__heading">
              <span className="pf-card__heading-icon">📅</span> Year-wise Projection
            </div>
            <div className="pf-table-wrap">
              <table className="pf-table">
                <thead>
                  <tr>
                    {['Year', 'Basic/mo', 'Emp. Contrib.', 'Empyr Contrib.', 'Interest', 'Balance'].map((h, i) => (
                      <th key={h} className={i === 0 ? 'pf-table__th--left' : 'pf-table__th--right'}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result?.rows?.length ? result.rows.map((r, i) => {
                    const isLast = i === result.rows.length - 1;
                    return (
                      <tr key={r.year} className={isLast ? 'pf-table__tr--highlight' : ''}>
                        <td className="pf-table__td--left pf-table__td--bold">{r.year}</td>
                        {[fmtShort(r.sal), fmtShort(r.empC), fmtShort(r.erC), fmtShort(r.int), fmtShort(r.bal)].map((v, j) => (
                          <td key={j} className="pf-table__td--right">{v}</td>
                        ))}
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan={6} className="pf-table__empty">Adjust inputs to see projections</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      {/* FOOTER */}
      <footer className="pf-footer">
        <div className="pf-footer__sources">
          Data sourced from <span>EPFO India</span> · <span>ClearTax</span> · <span>Groww</span> · <span>Bajaj Finserv</span> · <span>BankBazaar</span>
        </div>
        <div className="pf-footer__disclaimer">
          ⚠️ Estimation only. Actual EPF may vary. FY 2025–26 Rate: 8.25% p.a. | EPS capped at ₹15,000 wage ceiling. Consult EPFO or HR for exact figures.
        </div>
      </footer>
    </div>
  );
}
